import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { InfoTip } from "@/components/app/InfoTip";
import { Layers } from "lucide-react";

/**
 * Historical-upload picker — surfaces every Processed upload for the
 * selected brand, sorted by week_end_date DESC. Calls ``onChange`` with
 * the chosen upload_id (or empty string for "Latest").
 *
 * Lives next to the page H1 so users can quickly retrospect on any past
 * week's data without leaving the screen.
 */
export function UploadPicker({ brandId, value, onChange, testId = "upload-picker" }) {
  const [uploads, setUploads] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!brandId) {
      setUploads([]);
      return;
    }
    setLoading(true);
    api.get(`/uploads?brand_id=${brandId}`)
      .then((r) => {
        const items = (r.data || []).filter((u) => u.upload_status === "Processed");
        items.sort((a, b) =>
          (b.week_end_date || "").localeCompare(a.week_end_date || "")
          || (b.created_at || "").localeCompare(a.created_at || ""),
        );
        setUploads(items);
      })
      .catch(() => setUploads([]))
      .finally(() => setLoading(false));
  }, [brandId]);

  const selected = useMemo(
    () => uploads.find((u) => u.id === value),
    [uploads, value],
  );

  return (
    <div className="inline-flex items-center gap-2" data-testid={testId}>
      <span className="text-xs uppercase tracking-widest text-slate-500 inline-flex items-center gap-1">
        <Layers className="h-3.5 w-3.5" />
        Week
        <InfoTip term="UPLOAD_PICKER" />
      </span>
      <Select value={value || "__latest__"} onValueChange={(v) => onChange(v === "__latest__" ? "" : v)}>
        <SelectTrigger className="h-8 w-[260px] text-xs" data-testid={`${testId}-trigger`}>
          <SelectValue placeholder={loading ? "Loading…" : "Latest week"} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="__latest__" data-testid={`${testId}-latest`}>
            Latest week {uploads[0]?.week_end_date ? `(${uploads[0].week_end_date})` : ""}
          </SelectItem>
          {uploads.map((u) => (
            <SelectItem key={u.id} value={u.id} data-testid={`${testId}-opt-${u.id}`}>
              {u.week_start_date || "—"} → {u.week_end_date || "—"}
              {(u.source_files?.[0]) ? ` · ${u.source_files[0]}` : ""}
            </SelectItem>
          ))}
          {!uploads.length && !loading && (
            <SelectItem value="__none__" disabled>No processed uploads yet</SelectItem>
          )}
        </SelectContent>
      </Select>
      {selected && (
        <span className="text-[11px] text-slate-400">
          {(selected.processed_rows || 0).toLocaleString()} terms
        </span>
      )}
    </div>
  );
}
